import apiClient from '../services/api';
import type { PhotoMediaSize } from './photoMedia';

export const S3_KEY_PREFIX = 's3:';

export function isS3KeyReference(value: string): boolean {
  return value.startsWith(S3_KEY_PREFIX);
}

export function toS3KeyReference(objectKey: string): string {
  return `${S3_KEY_PREFIX}${objectKey}`;
}

/** Extrait une clé objet depuis une URL MinIO/S3 (y compris URL signée). */
export function extractObjectKeyFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const path = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
    const bucketPrefixes = ['inventwee-photos/'];
    for (const prefix of bucketPrefixes) {
      if (path.startsWith(prefix)) {
        const key = path.slice(prefix.length);
        if (isObjectKeyPattern(key)) return key;
      }
    }
    if (isObjectKeyPattern(path)) return path;
    return null;
  } catch {
    return null;
  }
}

export function isObjectKeyPattern(value: string): boolean {
  return /^tenant\/\d+\/items\/\d+\/.+/.test(value);
}

export function isInternalMinioUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname === 'minio' || hostname.startsWith('minio.');
  } catch {
    return false;
  }
}

/** Payload stable pour sync API — toujours préférer la clé S3 à une URL signée éphémère. */
export function photoPayloadForSave(photo: unknown): string {
  if (typeof photo === 'string') {
    if (photo.startsWith('local:')) return '';
    if (photo.startsWith(S3_KEY_PREFIX)) return photo;
    if (photo.startsWith('data:')) return photo;
    if (/^https?:\/\//i.test(photo)) {
      const key = extractObjectKeyFromUrl(photo);
      if (key) return toS3KeyReference(key);
      return photo;
    }
    return photo;
  }
  if (photo && typeof photo === 'object') {
    const row = photo as { s3Key?: string; url?: string };
    if (row.s3Key) return toS3KeyReference(row.s3Key);
    if (row.url) return photoPayloadForSave(row.url);
  }
  return '';
}

export function photoPayloadFromAny(photo: unknown): string {
  return photoPayloadForSave(photo);
}

export function photoPayloadsEqual(a: string, b: string): boolean {
  const norm = (value: string) => photoPayloadForSave(value);
  return norm(a) === norm(b);
}

export function havePhotosChanged(current: string[], original: string[]): boolean {
  if (current.length !== original.length) return true;
  return current.some((photo, index) => !photoPayloadsEqual(photo, original[index] || ''));
}

export function photoDisplayUrl(photo: string, previewByKey: Record<string, string>): string {
  if (photo.startsWith('local:')) {
    return photo.slice(6);
  }
  if (isS3KeyReference(photo)) {
    const key = photo.slice(S3_KEY_PREFIX.length);
    return previewByKey[key] || '';
  }
  if (isInternalMinioUrl(photo)) {
    return '';
  }
  return photo;
}

export async function fetchObjectReadUrl(
  objectKey: string,
  size: PhotoMediaSize = 'full',
): Promise<string | null> {
  try {
    const response = await apiClient.get('/storage/object-url', {
      params: { key: objectKey, size },
    });
    return response.data?.url ?? null;
  } catch {
    return null;
  }
}

export async function fetchStorageConfig(): Promise<{ directUpload: boolean; mode: string }> {
  try {
    const response = await apiClient.get('/storage/config');
    return {
      directUpload: response.data?.directUpload === true,
      mode: response.data?.mode || 'legacy',
    };
  } catch {
    return { directUpload: false, mode: 'legacy' };
  }
}

export async function uploadPhotoBlob(
  blob: Blob,
  options?: { tenantId?: number; itemId?: number; filename?: string },
): Promise<{ objectKey: string; readUrl: string; thumbUrl?: string; reference: string }> {
  const form = new FormData();
  form.append('file', blob, options?.filename || 'photo.jpg');

  const params = new URLSearchParams();
  if (options?.tenantId != null) params.set('tenantId', String(options.tenantId));
  if (options?.itemId != null) params.set('itemId', String(options.itemId));

  const response = await apiClient.post(`/storage/upload?${params.toString()}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const objectKey = response.data.objectKey as string;
  return {
    objectKey,
    readUrl: response.data.readUrl as string,
    thumbUrl: response.data.thumbUrl as string | undefined,
    reference: toS3KeyReference(objectKey),
  };
}

/** Compression upload : max 1200px, JPEG ~76 % — bon compromis poids / qualité viewer. */
export async function compressFileToBlob(file: File, maxWidth = 1200, quality = 0.76): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxWidth / bitmap.width);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Impossible de traiter l\'image');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Compression échouée'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}
